import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { providersService } from '../../shared/services/providers.service';
import { citiesService, categoriesService } from '../../shared/services/catalog.service';
import { ProviderCard } from '../../shared/components/ui/ProviderCard';
import { CategoryIcon } from '../../shared/components/ui/CategoryIcon';
import { useSelectedCountry } from '../../shared/hooks/useSelectedCountry';

export function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const cityId = params.get('cityId') || undefined;
  // Geoapify-sourced cities have no UUID — they ride on the `cityName` URL
  // param instead. The backend matches them against the denormalised
  // {@code city_name} column.
  const cityNameParam = params.get('cityName') || undefined;
  const categoryId = params.get('categoryId') || undefined;
  const qParam = params.get('q') || '';
  // Coordinates of the searched city, forwarded by the category search bar
  // (CategoryLandingPage sets `lat`/`lon` from the Geoapify pick). When
  // present they unlock the V48 travel layer — providers based in nearby
  // cities that cover this one. Absent → plain local search, as before.
  const latParam = params.get('lat') || undefined;
  const lonParam = params.get('lon') || undefined;
  // The visitor's chosen market is the single source of truth for what
  // country's providers we surface. We deliberately ignore any `country`
  // query-string param here — the persistent selection (top-bar dialog,
  // localStorage) wins so a French visitor on /search never sees German
  // providers regardless of the URL they landed on.
  const { countryCode: selectedCountry } = useSelectedCountry();
  const countryCode = (selectedCountry || '').toUpperCase() || undefined;

  // Local input mirrors the URL param so users see what they typed and we
  // can debounce updates to the URL/API without triggering one fetch per
  // keystroke.
  const [textInput, setTextInput] = useState(qParam);

  // Pull URL → input when the URL changes externally (e.g. browser back).
  useEffect(() => setTextInput(qParam), [qParam]);

  // Debounce input → URL (300 ms), then react-query's queryKey picks it up.
  useEffect(() => {
    const next = textInput.trim();
    if (next === qParam) return;
    const handle = window.setTimeout(() => {
      const merged = new URLSearchParams(params);
      if (next) merged.set('q', next);
      else merged.delete('q');
      setParams(merged, { replace: true });
    }, 300);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textInput]);

  const cities = useQuery({ queryKey: ['cities'], queryFn: () => citiesService.list() });
  const categories = useQuery({
    queryKey: ['categories', countryCode],
    queryFn: () => categoriesService.list(countryCode),
  });

  const query = useQuery({
    queryKey: [
      'providers', 'search',
      cityId, cityNameParam, categoryId, countryCode, qParam, latParam, lonParam,
    ],
    queryFn: () =>
      providersService.search({
        cityId,
        cityName: cityNameParam,
        categoryId,
        countryCode,
        q: qParam || undefined,
        searchLat: latParam ? Number(latParam) : undefined,
        searchLng: lonParam ? Number(lonParam) : undefined,
        page: 0,
        size: 24,
      }),
  });

  const list = query.data?.data ?? [];
  const total = query.data?.meta.pagination?.totalElements ?? list.length;

  const selectedCity = cities.data?.find((c) => c.id === cityId);
  // Display name for the chip: backend city when cityId is set, else the
  // free-text param from Photon.
  const cityChipLabel = selectedCity?.name ?? cityNameParam ?? null;
  const selectedCategory = categories.data?.find((c) => c.id === categoryId);
  const hasAnyFilter = Boolean(cityChipLabel || selectedCategory || qParam);

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          {t('search.title')}
        </Typography>
        {!query.isLoading && (
          <Typography variant="subtitle1" color="text.secondary">
            {t('search.results', { count: total })}
          </Typography>
        )}
      </Box>

      {/* Name search */}
      <TextField
        value={textInput}
        onChange={(e) => setTextInput(e.target.value)}
        placeholder={t('search.namePlaceholder')}
        fullWidth
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: textInput ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setTextInput('')}
                aria-label={t('common.clearFilters')}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />

      {/* Active filters */}
      {hasAnyFilter && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          {selectedCategory && (
            <Chip
              icon={
                <CategoryIcon
                  code={selectedCategory.code}
                  name={selectedCategory.name}
                  sx={{ fontSize: '1rem !important' }}
                />
              }
              label={selectedCategory.name}
              onDelete={() => updateParam('categoryId', null)}
              deleteIcon={<CloseRoundedIcon />}
              sx={{
                height: 36,
                fontSize: '0.9375rem',
                borderRadius: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? `${theme.palette.primary.main}22`
                    : `${theme.palette.primary.main}14`,
                color: 'primary.main',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'primary.main' },
              }}
            />
          )}
          {cityChipLabel && (
            <Chip
              icon={<LocationOnIcon sx={{ fontSize: '1rem !important' }} />}
              label={selectedCity
                ? `${selectedCity.name}, ${selectedCity.country}`
                : cityChipLabel}
              onDelete={() => {
                // Remove whichever city filter is active.
                if (cityId) updateParam('cityId', null);
                if (cityNameParam) updateParam('cityName', null);
              }}
              deleteIcon={<CloseRoundedIcon />}
              sx={{
                height: 36,
                fontSize: '0.9375rem',
                borderRadius: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? `${theme.palette.secondary.main}22`
                    : `${theme.palette.secondary.main}14`,
                color: 'secondary.main',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'secondary.main' },
              }}
            />
          )}
          {qParam && (
            <Chip
              icon={<SearchIcon sx={{ fontSize: '1rem !important' }} />}
              label={`"${qParam}"`}
              onDelete={() => {
                setTextInput('');
                updateParam('q', null);
              }}
              deleteIcon={<CloseRoundedIcon />}
              sx={{
                height: 36,
                fontSize: '0.9375rem',
                borderRadius: 2,
                bgcolor: 'action.hover',
                color: 'text.primary',
                fontWeight: 600,
              }}
            />
          )}
        </Stack>
      )}

      {/* Results */}
      {query.isLoading ? (
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : list.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: { xs: 6, md: 10 },
            px: 2,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 4,
          }}
        >
          <Box
            sx={{
              width: 88,
              height: 88,
              mx: 'auto',
              mb: 2.5,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}1F 0%, ${theme.palette.secondary.main}1F 100%)`,
              color: 'primary.main',
            }}
          >
            <SearchOffIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {cityChipLabel
              ? t('search.emptyForCity', { city: cityChipLabel })
              : t('search.empty')}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 460, mx: 'auto' }}>
            {cityChipLabel
              ? t('search.emptyForCityHint', { city: cityChipLabel })
              : t('search.emptyHint')}
          </Typography>
          {/* "Didn't find it? Post a request" — keeps unmet demand inside the
              platform instead of letting the visitor leave. Forwards the query
              + city name so the new-request form lands pre-filled. */}
          <Button
            onClick={() => {
              const params = new URLSearchParams();
              if (qParam.trim()) params.set('title', qParam.trim());
              if (cityChipLabel) params.set('cityName', cityChipLabel);
              navigate(`/requests/new${params.toString() ? '?' + params.toString() : ''}`);
            }}
            variant="contained"
            sx={{ mt: 3 }}
          >
            {t('search.postRequestCta')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {list.map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p.id}>
              <ProviderCard provider={p} searchedCityName={cityChipLabel ?? undefined} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Reset shortcut when filtered + empty */}
      {!query.isLoading && list.length === 0 && hasAnyFilter && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Chip
            label={t('common.clearFilters')}
            onClick={() => {
              setTextInput('');
              navigate('/search');
            }}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      )}
    </Container>
  );
}
